import { useMemo, useState } from "react";
import {
  FiBell,
  FiBookOpen,
  FiBox,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiFileText,
  FiHelpCircle,
  FiInbox,
  FiSearch,
  FiShield,
  FiTruck,
  FiUploadCloud,
  FiUserCheck,
  FiUsers,
} from "react-icons/fi";

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white shadow-sm transition-colors dark:border-slate-800 dark:bg-slate-900 ${className}`}
    >
      {children}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-blue-600 text-white">
        <Icon className="text-lg" />
      </div>

      <div>
        <h2 className="text-lg font-bold text-slate-950 dark:text-white">
          {title}
        </h2>

        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {subtitle}
        </p>
      </div>
    </div>
  );
}

const quickGuides = [
  {
    title: "Containers",
    description:
      "Create, search, filter, update, and view containers in table or grid view.",
    icon: FiBox,
  },
  {
    title: "Container Documents",
    description:
      "Upload, download, and manage Bills of Lading, invoices, customs files, and delivery documents.",
    icon: FiUploadCloud,
  },
  {
    title: "Customer Assignment",
    description:
      "Select one or more containers and assign them to a customer from the Containers page.",
    icon: FiUsers,
  },
  {
    title: "Supplier / Forwarder Access",
    description:
      "Assign selected containers to external users and control status-update and document-upload access.",
    icon: FiUserCheck,
  },
  {
    title: "Roles & Permissions",
    description:
      "Control page access and create, read, update, or delete permissions for each role.",
    icon: FiShield,
  },
  {
    title: "Email Documents",
    description:
      "Automatically attach emailed documents when the email contains a container, booking/BL, or serial number.",
    icon: FiInbox,
  },
  {
    title: "WhatsApp Alerts",
    description:
      "Send container delivery alerts to the recipient numbers saved in Settings.",
    icon: FiBell,
  },
  {
    title: "Other Logistics Modules",
    description:
      "Use tracking, forwarders, freight rates, invoices, payments, and reports for daily operations.",
    icon: FiTruck,
  },
];

const faqItems = [
  {
    question: "How do I upload a document to a container?",
    answer:
      "Open Containers and click the Documents icon, select the document type and files, then click Upload Documents.",
  },
  {
    question: "How do I assign containers to a customer?",
    answer:
      "Select the required container checkboxes, choose Customer, select the customer, and click Assign to Customer.",
  },
  {
    question: "How do I give a supplier or forwarder access?",
    answer:
      "Set the user to Assigned containers only, select containers, choose Supplier / Forwarder, select the user, choose permissions, and grant access.",
  },
  {
    question: "What can an assigned-only user access?",
    answer:
      "They can only open containers assigned to them. Their status-update and document-upload access depends on the permissions granted for each container.",
  },
  {
    question: "How does email document import work?",
    answer:
      "Enable Email Documents in Settings. The sender should include the container number, booking/BL number, or serial number in the email subject or file name.",
  },
  {
    question: "How do WhatsApp alerts work?",
    answer:
      "One connected WhatsApp sender number sends alerts to the recipient numbers entered in Settings.",
  },
  {
    question: "Why is a page or action hidden?",
    answer:
      "The user's role does not have the required permission. Update the role in Settings under Roles & Permissions.",
  },
  {
    question: "Where do I manage users?",
    answer:
      "Open Settings, then User Access. Create or edit a user, select the role, container access type, phone number, and active status.",
  },
];

const bestPractices = [
  "Use Assigned containers only for suppliers and forwarders.",
  "Grant only the permissions required for each role.",
  "Use clear container, booking/BL, and document names.",
  "Keep WhatsApp recipients and approved email senders updated.",
  "Review container assignments before granting external access.",
];

export default function Help() {
  const [search, setSearch] = useState("");
  const [openQuestion, setOpenQuestion] = useState(0);

  const normalizedSearch = search.trim().toLowerCase();

  const filteredGuides = useMemo(() => {
    if (!normalizedSearch) return quickGuides;

    return quickGuides.filter((guide) =>
      `${guide.title} ${guide.description}`
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [normalizedSearch]);

  const filteredFaq = useMemo(() => {
    if (!normalizedSearch) return faqItems;

    return faqItems.filter((item) =>
      `${item.question} ${item.answer}`
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [normalizedSearch]);

  return (
    <div className="space-y-6 p-6 text-slate-900 dark:text-slate-100">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
            Help & Support
          </h1>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Simple guidance for the main logistics portal features.
          </p>
        </div>

        <div className="flex h-11 w-full items-center gap-3 rounded-full border border-slate-200 bg-white px-4 shadow-sm dark:border-slate-700 dark:bg-slate-900 lg:w-80">
          <FiSearch className="shrink-0 text-slate-400" />

          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search help..."
            className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-slate-100"
          />
        </div>
      </div>

      <Card className="p-5">
        <SectionHeader
          icon={FiBookOpen}
          title="Quick Guide"
          subtitle="Main functions available in the portal."
        />

        {filteredGuides.length === 0 ? (
          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
            No guide found.
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {filteredGuides.map((guide) => {
              const Icon = guide.icon;

              return (
                <div
                  key={guide.title}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950"
                >
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300">
                    <Icon />
                  </div>

                  <h3 className="mt-4 font-bold text-slate-950 dark:text-white">
                    {guide.title}
                  </h3>

                  <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    {guide.description}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <Card className="p-5">
          <SectionHeader
            icon={FiHelpCircle}
            title="Frequently Asked Questions"
            subtitle="Short answers for common tasks."
          />

          <div className="mt-5 space-y-3">
            {filteredFaq.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
                No help topic found.
              </div>
            ) : (
              filteredFaq.map((item, index) => {
                const isOpen = openQuestion === index;

                return (
                  <button
                    key={item.question}
                    type="button"
                    onClick={() =>
                      setOpenQuestion(isOpen ? -1 : index)
                    }
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 text-left transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-semibold text-slate-950 dark:text-white">
                        {item.question}
                      </span>

                      {isOpen ? (
                        <FiChevronDown className="shrink-0 text-slate-400" />
                      ) : (
                        <FiChevronRight className="shrink-0 text-slate-400" />
                      )}
                    </div>

                    {isOpen ? (
                      <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
                        {item.answer}
                      </p>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </Card>

        <Card className="p-5">
          <SectionHeader
            icon={FiCheckCircle}
            title="Best Practices"
            subtitle="Keep access and records organised."
          />

          <div className="mt-5 space-y-3">
            {bestPractices.map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950"
              >
                <FiCheckCircle className="mt-0.5 shrink-0 text-emerald-500" />

                <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <SectionHeader
          icon={FiFileText}
          title="Document Matching"
          subtitle="Use clear references so documents are attached correctly."
        />

        <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
          {[
            "Container number",
            "Booking / BL number",
            "Serial number",
          ].map((item) => (
            <div
              key={item}
              className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
            >
              <FiCheckCircle className="shrink-0" />
              {item}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}